import React, { useEffect, useMemo } from 'react';

type Props = {
  isImpressed: boolean;
  onImpressionStart: () => void;
  /**
   * 몇 퍼센트 이상 화면에 표시되면 "보인다"고 생각할지 결정합니다.
   * 예를 들어, 0.5가 주어지면 0.5 이상이 보여졌을 때 `onImpressionStart`, 이하가 보여졌을 때 `onImpressionEnd`가 호출됩니다.
   * @default
   */
  impressionRatio?: number;
  onImpressionEnd?: () => void;
  children?: React.ReactNode;
};

export default function ImpressionArea({
  isImpressed,
  onImpressionStart,
  onImpressionEnd,
  impressionRatio = 0,
  children,
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null);

  const observer = useMemo(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const handleImpression = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && isImpressed) {
          onImpressionStart();

          return;
        }
        onImpressionEnd?.();
      });
    };

    return new IntersectionObserver(handleImpression, {
      root: null,
      threshold: impressionRatio,
    });
  }, [impressionRatio, isImpressed, onImpressionEnd, onImpressionStart]);

  useEffect(() => {
    if (ref.current && observer) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current && observer) {
        observer.unobserve(ref.current);
      }
    };
  }, [observer]);

  return <div ref={ref}>{children}</div>;
}
